import { Routes, Route, Navigate, useParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "./lib/AuthContext";
import { WorkspaceProvider, useWorkspace } from "./contexts/WorkspaceContext";
import { CHEF_ENABLED } from "./lib/routes";
import { ProfileProvider } from "./contexts/ProfileContext";
import { AppLayout, HubLayout } from "./components/layout";
import LoginPage from "./components/LoginPage";
import { WorkspaceProblem } from "./components/WorkspaceProblem";
import { InvitePage } from "./pages/InvitePage";
// Dashboard component removed — workspace dashboard is the main view
import PricingPage from "./components/PricingPage";
import BillingSettings from "./components/BillingSettings";
import BillingHistory from "./components/BillingHistory";
import BoostsHistory from "./components/BoostsHistory";
import BillingVisibleRoute from "./components/BillingVisibleRoute";

// NaraHub App Pages
import {
  AppDashboard,
  Projects,
  ProjectDetail,
  ProjectCollaborators,
  ProductCatalog,
  DocumentDetail,
  TranscriptEditor,
  MarkdownEditor,
  Trash,
  WorkspaceMembers,
  BusinessProfiles,
  CollaboratorDetail,
  UserProfile,
  WorkspaceSettings,
  WorkspaceTags,
  NaraShape,
  NaraShapeTemplate,
  AdminWorkspaces,
  AdminWorkspaceDetail,
  AdminCollaboratorDetail,
  AdminProjectDetail,
  AdminUsers,
  AdminUserDetail,
  ProjectTemplates,
  ProjectTemplateDetail,
  ProjectGenerate,
  DocumentGenerate,
  RecipeChef,
  AdminWizards,
  AdminWizardDetail,
  AdminWizardHelp,
  AdminWizardEntryDetail,
  AdminAppSettings,
  AdminInfrastructure,
  AdminArchitecture,
  AdminBoostUsage,
  AdminActivity,
  AdminBoostPricing,
  AdminStripeProducts,
  AdminTrials,
  AdminWorkflows,
  WizardPage,
  WizardBySlugPage,
} from "./pages";
import { WorkspaceNotFound } from "./pages/WorkspaceNotFound";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Bypass auth in development mode
  if (import.meta.env.DEV) {
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (user) {
    // Redirect back to the page the user was trying to access, or root
    const fromLocation = (location.state as { from?: { pathname: string; search?: string } })?.from;
    const from = fromLocation ? fromLocation.pathname + (fromLocation.search || "") : "/";
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}

/**
 * Root redirect component.
 * Redirects logged-in users to their workspace dashboard.
 */
function RootRedirect() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to a temporary component that will handle workspace routing
  return (
    <WorkspaceProvider>
      <WorkspaceRedirect />
    </WorkspaceProvider>
  );
}

/**
 * Redirects to the user's workspace dashboard, or provisions an onboarding wizard.
 */
function WorkspaceRedirect() {
  const { workspaces, activeWorkspace, isLoading, error, noAccess, refreshWorkspaces } = useWorkspace();
  const navigate = useNavigate();
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState<string | null>(null);

  const provisionWizard = useCallback(async () => {
    setProvisioning(true);
    setProvisionError(null);
    try {
      const { provisionOnboardingWizard } = await import("./services/naraDoor");
      const result = await provisionOnboardingWizard();
      navigate(`/wizard/${result.instance_id}`, { replace: true });
    } catch (err) {
      setProvisionError(err instanceof Error ? err.message : "Failed to start onboarding");
      setProvisioning(false);
    }
  }, [navigate]);

  useEffect(() => {
    // A failed load or a removed user has no workspace to provision an onboarding wizard for
    if (isLoading || provisioning || error || noAccess) return;

    // Has a workspace with a handle → go to dashboard
    const workspace = activeWorkspace || workspaces[0];
    if (workspace?.handle) {
      navigate(`/ws/${workspace.handle}/dashboard`, { replace: true });
      return;
    }

    // No workspace or no handle → provision onboarding wizard
    provisionWizard();
  }, [workspaces, activeWorkspace, isLoading, error, noAccess, navigate, provisioning, provisionWizard]);

  if (noAccess || error) return <WorkspaceProblem noAccess={noAccess} onRetry={refreshWorkspaces} />;

  if (provisionError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-red-600 text-sm">{provisionError}</p>
        <button
          onClick={provisionWizard}
          className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );
}

/**
 * Workspace route wrapper that extracts handle from URL and provides context.
 */
function WorkspaceRouteWrapper({ children }: { children: React.ReactNode }) {
  const { workspaceHandle } = useParams<{ workspaceHandle: string }>();
  const { user, loading } = useAuth();
  const location = useLocation();

  // Bypass auth in development mode
  if (import.meta.env.DEV) {
    return (
      <WorkspaceProvider workspaceHandle={workspaceHandle}>
        <WorkspaceGuard>{children}</WorkspaceGuard>
      </WorkspaceProvider>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return (
    <WorkspaceProvider workspaceHandle={workspaceHandle}>
      <WorkspaceGuard>{children}</WorkspaceGuard>
    </WorkspaceProvider>
  );
}

/**
 * Guard component that checks if workspace handle is valid.
 */
function WorkspaceGuard({ children }: { children: React.ReactNode }) {
  const { workspaceHandle } = useParams<{ workspaceHandle: string }>();
  const { invalidHandle, isLoading, noAccess, error, refreshWorkspaces } = useWorkspace();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (noAccess || error) return <WorkspaceProblem noAccess={noAccess} onRetry={refreshWorkspaces} />;

  if (invalidHandle && workspaceHandle) {
    return <WorkspaceNotFound handle={workspaceHandle} />;
  }

  return <AppLayout>{children}</AppLayout>;
}

/**
 * Admin route wrapper (for /admin/* routes).
 * These routes are workspace-independent but still need workspace context for navigation.
 */
function AdminRouteWrapper({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Bypass auth in development mode
  if (import.meta.env.DEV) {
    return (
      <WorkspaceProvider>
        <HubLayout>{children}</HubLayout>
      </WorkspaceProvider>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return (
    <WorkspaceProvider>
      <HubLayout>{children}</HubLayout>
    </WorkspaceProvider>
  );
}

function App() {
  return (
    <ProfileProvider>
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      {/* Invite link: handles its own sign-in round trip, so no ProtectedRoute */}
      <Route path="/invite" element={<InvitePage />} />
      {/* /dashboard redirects to workspace dashboard via root */}
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      <Route
        path="/pricing"
        element={
          <ProtectedRoute>
            <BillingVisibleRoute>
              <PricingPage />
            </BillingVisibleRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <BillingVisibleRoute>
              <BillingSettings />
            </BillingVisibleRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing-history"
        element={
          <ProtectedRoute>
            <BillingVisibleRoute>
              <BillingHistory />
            </BillingVisibleRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/boosts-history"
        element={
          <ProtectedRoute>
            <BillingVisibleRoute>
              <BoostsHistory />
            </BillingVisibleRoute>
          </ProtectedRoute>
        }
      />

      {/* Workspace-scoped routes: /ws/:workspaceHandle/* */}
      <Route path="/ws/:workspaceHandle/*" element={<WorkspaceRouteWrapper><WorkspaceRoutes /></WorkspaceRouteWrapper>} />

      {/* Server-driven wizard (slug-based: provisions instance automatically) */}
      <Route
        path="/wizard/s/:slug"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <WizardBySlugPage />
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />

      {/* Server-driven wizard (instance-based) */}
      <Route
        path="/wizard/:instanceId"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <WizardPage />
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />

      {/* User Profile */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <UserProfile />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />

      {/* Workspace Settings */}
      <Route
        path="/workspace/settings"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <WorkspaceSettings />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />

      {/* Workspace Tags */}
      <Route
        path="/workspace/tags"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <WorkspaceTags />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes (workspace-independent) */}
      <Route
        path="/admin/users"
        element={
          <AdminRouteWrapper>
            <AdminUsers />
          </AdminRouteWrapper>
        }
      />
      <Route
        path="/admin/users/:userId"
        element={
          <AdminRouteWrapper>
            <AdminUserDetail />
          </AdminRouteWrapper>
        }
      />
      <Route
        path="/admin/workspaces"
        element={
          <AdminRouteWrapper>
            <AdminWorkspaces />
          </AdminRouteWrapper>
        }
      />
      <Route
        path="/admin/workspaces/:id"
        element={
          <AdminRouteWrapper>
            <AdminWorkspaceDetail />
          </AdminRouteWrapper>
        }
      />
      <Route
        path="/admin/workspaces/:workspaceId/collaborators/:userId"
        element={
          <AdminRouteWrapper>
            <AdminCollaboratorDetail />
          </AdminRouteWrapper>
        }
      />
      <Route
        path="/admin/workspaces/:workspaceId/projects/:projectId"
        element={
          <AdminRouteWrapper>
            <AdminProjectDetail />
          </AdminRouteWrapper>
        }
      />
      {/* Document Recipes (NaraShape) */}
      <Route
        path="/admin/document-recipes"
        element={
          <AdminRouteWrapper>
            <NaraShape />
          </AdminRouteWrapper>
        }
      />
      <Route
        path="/admin/document-recipes/:id"
        element={
          <AdminRouteWrapper>
            <NaraShapeTemplate />
          </AdminRouteWrapper>
        }
      />
      {/* Recipe Chef — disabled while /chef/* backend is offline (see lib/routes CHEF_ENABLED) */}
      {CHEF_ENABLED && (
        <Route
          path="/admin/chef"
          element={
            <AdminRouteWrapper>
              <RecipeChef />
            </AdminRouteWrapper>
          }
        />
      )}
      {/* Wizard Entry Detail */}
      <Route
        path="/admin/wizard-entries/:entryId"
        element={
          <AdminRouteWrapper>
            <AdminWizardEntryDetail />
          </AdminRouteWrapper>
        }
      />
      {/* Wizard Help (must come before :wizardId to match first) */}
      <Route
        path="/admin/wizards/help"
        element={
          <AdminRouteWrapper>
            <AdminWizardHelp />
          </AdminRouteWrapper>
        }
      />
      {/* Wizard Detail (must come before /admin/wizards to avoid matching the list route) */}
      <Route
        path="/admin/wizards/:wizardId"
        element={
          <AdminRouteWrapper>
            <AdminWizardDetail />
          </AdminRouteWrapper>
        }
      />
      {/* Wizards */}
      <Route
        path="/admin/wizards"
        element={
          <AdminRouteWrapper>
            <AdminWizards />
          </AdminRouteWrapper>
        }
      />
      {/* Admin Settings */}
      <Route
        path="/admin/settings"
        element={
          <AdminRouteWrapper>
            <AdminAppSettings />
          </AdminRouteWrapper>
        }
      />
      {/* Admin Infrastructure */}
      <Route
        path="/admin/infrastructure"
        element={
          <AdminRouteWrapper>
            <AdminInfrastructure />
          </AdminRouteWrapper>
        }
      />
      {/* Admin Architecture */}
      <Route
        path="/admin/architecture"
        element={
          <AdminRouteWrapper>
            <AdminArchitecture />
          </AdminRouteWrapper>
        }
      />
      {/* Boost Usage */}
      <Route
        path="/admin/boost-usage"
        element={
          <AdminRouteWrapper>
            <AdminBoostUsage />
          </AdminRouteWrapper>
        }
      />
      {/* Boost Pricing */}
      <Route
        path="/admin/boost-pricing"
        element={
          <AdminRouteWrapper>
            <AdminBoostPricing />
          </AdminRouteWrapper>
        }
      />
      {/* Stripe Products */}
      <Route
        path="/admin/stripe-products"
        element={
          <AdminRouteWrapper>
            <AdminStripeProducts />
          </AdminRouteWrapper>
        }
      />
      {/* Workspace activity */}
      <Route
        path="/admin/activity"
        element={
          <AdminRouteWrapper>
            <AdminActivity />
          </AdminRouteWrapper>
        }
      />
      {/* Trials */}
      <Route
        path="/admin/trials"
        element={
          <AdminRouteWrapper>
            <AdminTrials />
          </AdminRouteWrapper>
        }
      />
      {/* Transcription workflows */}
      <Route
        path="/admin/workflows"
        element={
          <AdminRouteWrapper>
            <AdminWorkflows />
          </AdminRouteWrapper>
        }
      />
      {/* Project Templates */}
      <Route
        path="/admin/project-templates"
        element={
          <AdminRouteWrapper>
            <ProjectTemplates />
          </AdminRouteWrapper>
        }
      />
      <Route
        path="/admin/project-templates/:id"
        element={
          <AdminRouteWrapper>
            <ProjectTemplateDetail />
          </AdminRouteWrapper>
        }
      />

      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />

      {/* Catch-all: redirect unknown paths */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </ProfileProvider>
  );
}

/**
 * Nested workspace routes.
 */
function WorkspaceRoutes() {
  return (
    <Routes>
      <Route path="dashboard" element={<AppDashboard />} />
      <Route path="projects" element={<Projects />} />
      <Route path="project/:id" element={<ProjectDetail />} />
      <Route path="project/:id/collaborators" element={<ProjectCollaborators />} />
      <Route path="project/:id/generate" element={<ProjectGenerate />} />
      <Route path="project/:id/catalog" element={<ProductCatalog />} />
      <Route path="project/:projectId/document/:id/generate" element={<DocumentGenerate />} />
      <Route path="project/:projectId/document/:id" element={<DocumentDetail />} />
      <Route path="project/:projectId/document/:id/transcript/edit" element={<TranscriptEditor />} />
      <Route path="project/:projectId/document/:id/edit" element={<MarkdownEditor />} />
      <Route path="trash" element={<Trash />} />
      <Route path="collaborators" element={<WorkspaceMembers />} />
      <Route path="collaborators/:id" element={<CollaboratorDetail />} />
      <Route path="business-profiles" element={<BusinessProfiles />} />
      {/* Default route within workspace */}
      <Route path="" element={<Navigate to="dashboard" replace />} />
    </Routes>
  );
}

export default App;
